import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getEffectiveSpaceTypes } from 'src/components/SearchBar/SpaceTypeFilters'
import {
  DEFAULT_CENTER,
  MAP_PAGE_SIZE,
  NEAR_USER,
  SEARCH_GRID_SIZE
} from 'src/constants'
import { IQueryParams } from 'src/types/types'
import { canonicalCity, canonicalHood } from 'src/utils/pixels/common'
import { getVertical } from 'src/utils/pixels/enrichment'
import fbp from 'src/utils/pixels/fbp'
import ga from 'src/utils/pixels/gtag'
import ms from 'src/utils/pixels/uetq'
import { bucketResultsCount, pillarFromPath } from 'src/utils/searchAnalytics'
import { CARD_LOCATION_FIELDS } from '../constants/fragments'
import {
  IAddress,
  IGooglePlaceDetails,
  ILocation,
  IQuery,
  IQueryGooglePlaceDetailsArgs,
  IQuerySearchLocationsArgs,
  Maybe
} from '../types/graphql'
import { useFilterProvider } from './FilterProvider'
import useLogger from './LoggerProvider/useLogger'

const getMode = (mode: Maybe<IAddress> | undefined, filters: IQueryParams) => {
  return {
    search:
      canonicalCity(mode?.city) ??
      mode?.colonia ??
      mode?.neighborhood ??
      filters?.search ??
      undefined,
    description:
      canonicalCity(mode?.city) ??
      mode?.colonia ??
      mode?.neighborhood ??
      filters?.description ??
      undefined,
    hood: mode?.neighborhood ?? mode?.colonia ?? filters?.hood ?? undefined,
    city: mode?.city ?? filters?.city ?? undefined,
    zipcode: mode?.zipcode ?? filters?.zipcode ?? undefined,
    state: mode?.state ?? filters?.state ?? undefined
  }
}

const SEARCH_LOCATIONS = gql`
  ${CARD_LOCATION_FIELDS}
  query SearchLocations(
    $pagination: PaginationInput!
    $searchInput: SearchLocationsInput!
    $placeId: String!
    $fetchPlace: Boolean!
  ) {
    searchLocations(pagination: $pagination, searchInput: $searchInput) {
      total
      hits {
        ...CardLocationFields
      }
      mode {
        neighborhood
        colonia
        city
        state
        zipcode
      }
    }
    googlePlaceDetails(placeId: $placeId) @include(if: $fetchPlace) {
      placeId
      lat
      lng
      zoom
    }
  }
`

type ILocationsContext = {
  requested: boolean
  loading: boolean
  placeDetails: IGooglePlaceDetails | undefined
  totalLocations: number | undefined
  pageLocations: ILocation[] | undefined
  allLocations: ILocation[]
  selectedLocation: ILocation | undefined
  setSelectedLocation: (location?: ILocation) => void
  mode: {
    search: string | undefined
    description: string | undefined
    hood: string | undefined
    city: string | undefined
    zipcode: string | undefined
    state: string | undefined
  }
}

type IProps = {
  skip?: boolean
  userLocation?: IGooglePlaceDetails
}

export const useLocations = ({ skip, userLocation }: IProps) => {
  const [requested, setRequested] = useState<boolean>(false)
  const [allLocations, setAllLocations] = useState<ILocation[]>([])
  const logger = useLogger()
  const { isReady, filters } = useFilterProvider()
  const [hashedFilters, setHashedFilters] = useState<string>('')

  const setLocationsStore = useCallback(
    (locations: ILocation[]) => {
      const uniqueLocs = [...locations, ...allLocations].reduce(
        (acc, loc) => {
          if (!acc[loc.id]) {
            acc[loc.id] = loc
          }
          return acc
        },
        {} as Record<string, ILocation>
      )
      setAllLocations(Object.values(uniqueLocs))
    },
    [allLocations]
  )

  useEffect(() => {
    if (!isReady) return
    // Exclude map-controlled fields (page, lat, lng, zoom) from the hash so
    // panning/zooming doesn't wipe the accumulated markers and retrigger the
    // Apollo query via a variable change. User-driven filter changes
    // (sortBy, sizes, price, spaceTypes, hood, city, etc.) still reset it.
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
      page,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
      lat,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
      lng,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
      zoom,
      ...rest
    } = filters
    const newHashedFilters = JSON.stringify(rest)
    if (newHashedFilters !== hashedFilters) {
      setAllLocations([])
      setHashedFilters(newHashedFilters)
    }
  }, [filters, hashedFilters, isReady])

  const {
    placeId: placeIdTemp,
    lat,
    lng,
    zoom,
    canStoreVehicle,
    maxPrice,
    minPrice,
    sortBy,
    sizes,
    page,
    locationTypes
  } = filters
  const query = lat && lng ? { lat, lng } : undefined
  // When a placeId is present, don't fall back to DEFAULT_CENTER — let the
  // backend resolve the placeId to coordinates via Google Places.  Sending
  // DEFAULT_CENTER (Monterrey) alongside a different city's placeId causes the
  // backend to search around Monterrey instead of the selected city.
  const center =
    placeIdTemp === NEAR_USER
      ? (userLocation ?? DEFAULT_CENTER)
      : (query ?? (placeIdTemp ? undefined : DEFAULT_CENTER))
  const placeId = filters.placeId !== NEAR_USER ? placeIdTemp : undefined

  const [controller] = useState(new AbortController())
  useEffect(() => {
    return () => controller.abort()
  }, [controller])

  const {
    loading,
    data,
    error: searchError
  } = useQuery<
    Pick<IQuery, 'searchLocations' | 'googlePlaceDetails'>,
    IQuerySearchLocationsArgs &
      IQueryGooglePlaceDetailsArgs & { fetchPlace: boolean }
  >(SEARCH_LOCATIONS, {
    skip: !isReady || skip,
    fetchPolicy: 'cache-and-network',
    context: { signal: controller.signal },
    variables: {
      pagination: {
        from: (page - 1) * SEARCH_GRID_SIZE,
        pageSize: MAP_PAGE_SIZE
      },
      searchInput: {
        placeId,
        center: center ? { lat: center.lat, lng: center.lng } : undefined,
        zoom,
        canStoreVehicle,
        sortBy,
        sizes,
        priceRange:
          maxPrice || minPrice ? { min: minPrice, max: maxPrice } : undefined,
        locationTypes
      },
      placeId: placeId ?? '',
      fetchPlace: !!placeId && placeId !== NEAR_USER
    }
  })

  // Track the last logged error message so RetryLink oscillations don't
  // spam Datadog and don't re-fire setState on every re-render.
  const lastLoggedErrorRef = useRef<string | null>(null)
  useEffect(() => {
    if (!searchError) {
      lastLoggedErrorRef.current = null
      return
    }
    const key = searchError.message ?? String(searchError)
    if (lastLoggedErrorRef.current === key) return
    lastLoggedErrorRef.current = key
    setRequested(true)
    logger.error('Error retrieving locations.', searchError)
  }, [searchError, logger])

  // Scroll-to-top decision lives in SearchManager (it owns the trigger-source
  // signal via `userFilterSig`, so it can distinguish filter changes from map
  // pans — Airbnb/Zillow pattern). This effect just delivers data and fires
  // analytics; it must not touch scroll position.
  useEffect(() => {
    if (!data?.searchLocations) return
    const { hits, mode, total } = data.searchLocations
    setRequested(true)
    setLocationsStore(hits as ILocation[])
    // results_count + bucket + primary_location_type mirror the SSG-init
    // fire in `SearchManager/index.tsx` so PostHog funnels can filter on
    // `results_count > 0` across both fire sites uniformly (dynamic
    // re-fetches were silently missing the property before). Bucket helper
    // is shared via `src/utils/searchAnalytics.ts`.
    const resultsCount = total ?? 0
    ga.event('search_completed', {
      ...filters,
      ...getMode(mode, filters),
      spaceTypes: getEffectiveSpaceTypes(filters),
      results_count: resultsCount,
      results_count_bucket: bucketResultsCount(resultsCount),
      primary_location_type: filters.locationTypes?.[0],
      // Override the raw `...filters` city/hood with canonical slugs so PostHog
      // groups CDMX variants (ciudad-de-mexico / ciudad-de-méxico / "ciudad de
      // mexico") as one. `pillar` is parsed from the route — the only pillar
      // signal that survives zero-result searches. See docs/marketing/liquidity.md.
      city: canonicalCity(filters.city),
      hood: canonicalHood(filters.hood),
      pillar: pillarFromPath()
    })
    ms.event('search', {
      category: 'search',
      label: 'search'
    })
    fbp.event('Search', {
      search_string: filters.search ?? filters.description ?? '',
      content_category: getVertical(
        (hits as ILocation[])[0]?.defaultConfiguration?.locationType
      ),
      content_type: 'product',
      contents: (hits as ILocation[]).slice(0, 5).map((loc: ILocation) => ({
        id: loc.defaultConfiguration?.id,
        quantity: loc.defaultConfiguration?.availability || 1
      })),
      content_ids: (hits as ILocation[])
        .slice(0, 5)
        .map((loc: ILocation) => loc.defaultConfiguration?.id)
        .filter(Boolean)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.searchLocations])

  const [selectedLocation, setSelectedLocation] = useState<ILocation>()

  const value = useMemo((): ILocationsContext => {
    const { mode } = data?.searchLocations ?? {}
    return {
      placeDetails: data?.googlePlaceDetails as IGooglePlaceDetails | undefined,
      loading,
      requested,
      totalLocations: data?.searchLocations?.total,
      pageLocations: data?.searchLocations?.hits as ILocation[] | undefined,
      allLocations,
      selectedLocation,
      setSelectedLocation,
      mode: getMode(mode, filters)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data, selectedLocation, allLocations, requested])

  return value
}
